import { useState } from "react";
import { X, Building2, ArrowRight, AlertTriangle } from "lucide-react";
import type { Lead, OrganizationType, OrganizationTier } from "../types";
import { ORGANIZATION_TYPES, ORGANIZATION_TIERS, LEAD_TIER_TO_ORG_TIER, TIER_EMOJI } from "../constants";
import { LeadStatusBadge } from "./LeadStatusBadge";
import { useLocale } from "../../../context/LanguageContext";

interface ConvertLeadDialogProps {
  open: boolean;
  lead: Lead;
  onClose: () => void;
  onConfirm: (input: {
    leadId: string;
    organizationType: OrganizationType;
    organizationTier?: OrganizationTier;
    adminFirstName: string;
    adminLastName: string;
    adminDepartment: string;
    adminJobTitle?: string;
    description?: string;
    website?: string;
  }) => void;
  loading: boolean;
  error?: string | null;
}

export function ConvertLeadDialog({
  open,
  lead,
  onClose,
  onConfirm,
  loading,
  error,
}: ConvertLeadDialogProps) {
  const { t } = useLocale();
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Form state
  const [orgType, setOrgType] = useState<OrganizationType>("COMMERCIAL");
  const [orgTier, setOrgTier] = useState<OrganizationTier>(
    LEAD_TIER_TO_ORG_TIER[lead.tier] ?? "SEED",
  );
  const [adminFirstName, setAdminFirstName] = useState(
    lead.name.split(" ")[0] ?? "",
  );
  const [adminLastName, setAdminLastName] = useState(
    lead.name.split(" ").slice(1).join(" ") ?? "",
  );
  const [department, setDepartment] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState("");

  if (!open) return null;

  const canProceedStep1 = orgType !== undefined;
  const canProceedStep2 =
    adminFirstName.trim() !== "" &&
    adminLastName.trim() !== "" &&
    department.trim() !== "";

  const handleSubmit = () => {
    onConfirm({
      leadId: lead._id,
      organizationType: orgType,
      organizationTier: orgTier,
      adminFirstName: adminFirstName.trim(),
      adminLastName: adminLastName.trim(),
      adminDepartment: department.trim(),
      adminJobTitle: jobTitle.trim() || undefined,
      description: description.trim() || undefined,
      website: website.trim() || undefined,
    });
  };

  const handleClose = () => {
    setStep(1);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={handleClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-lg border border-border bg-card shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold text-foreground">
                {t("leads.convert.title")}
              </h2>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Step indicator */}
          <div className="border-b border-border px-6 py-3">
            <div className="flex items-center gap-2 text-xs font-medium">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                      s === step
                        ? "bg-primary text-primary-foreground"
                        : s < step
                          ? "bg-primary/20 text-primary"
                          : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {s}
                  </span>
                  <span className={s === step ? "text-foreground" : "text-muted-foreground"}>
                    {t(`leads.convert.step${s}`)}
                  </span>
                  {s < 3 && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                </div>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="space-y-4 px-6 py-5">
            {/* Server error banner */}
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                <span>{error}</span>
              </div>
            )}
            {/* Step 1: Organization Details */}
            {step === 1 && (
              <>
                <div className="rounded-md bg-muted/50 p-3 text-sm">
                  <p className="font-medium text-foreground">{lead.company}</p>
                  <p className="text-muted-foreground">
                    {lead.name} · {lead.email} · {TIER_EMOJI[lead.tier]} {t(`leads.tier.${lead.tier}`)}
                  </p>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t("leads.convert.orgType")}
                  </label>
                  <select
                    value={orgType}
                    onChange={(e) => setOrgType(e.target.value as OrganizationType)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    {ORGANIZATION_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {t(`leads.convert.orgTypes.${type}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t("leads.convert.orgTier")}
                  </label>
                  <select
                    value={orgTier}
                    onChange={(e) => setOrgTier(e.target.value as OrganizationTier)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    {ORGANIZATION_TIERS.map((tier) => (
                      <option key={tier} value={tier}>
                        {t(`leads.tier.${tier}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t("leads.convert.description")}
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t("leads.convert.descriptionPlaceholder")}
                    rows={2}
                    maxLength={2000}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t("leads.convert.website")}
                  </label>
                  <input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://example.com"
                    maxLength={200}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </>
            )}

            {/* Step 2: Admin Contact */}
            {step === 2 && (
              <>
                <p className="text-sm text-muted-foreground">
                  {t("leads.convert.adminInfo")}
                </p>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">
                      {t("leads.convert.firstName")}
                    </label>
                    <input
                      type="text"
                      value={adminFirstName}
                      onChange={(e) => setAdminFirstName(e.target.value)}
                      maxLength={50}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">
                      {t("leads.convert.lastName")}
                    </label>
                    <input
                      type="text"
                      value={adminLastName}
                      onChange={(e) => setAdminLastName(e.target.value)}
                      maxLength={50}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t("leads.convert.email")}
                  </label>
                  <input
                    type="email"
                    value={lead.email}
                    disabled
                    className="w-full rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("leads.convert.emailHint")}
                  </p>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t("leads.convert.department")} *
                  </label>
                  <input
                    type="text"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    placeholder={t("leads.convert.departmentPlaceholder")}
                    maxLength={100}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t("leads.convert.jobTitle")}
                  </label>
                  <input
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder={t("leads.convert.jobTitlePlaceholder")}
                    maxLength={100}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </>
            )}

            {/* Step 3: Review & Confirm */}
            {step === 3 && (
              <>
                <p className="text-sm text-muted-foreground">
                  {t("leads.convert.reviewDescription")}
                </p>

                <div className="space-y-3 rounded-md border border-border p-4">
                  <h4 className="text-sm font-semibold text-foreground">
                    {t("leads.convert.orgSection")}
                  </h4>
                  <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                    <div>
                      <p className="text-muted-foreground">{t("leads.table.company")}</p>
                      <p className="font-medium text-foreground">{lead.company}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t("leads.convert.orgType")}</p>
                      <p className="font-medium text-foreground">{t(`leads.convert.orgTypes.${orgType}`)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t("leads.table.tier")}</p>
                      <p className="font-medium text-foreground">{t(`leads.tier.${orgTier}`)}</p>
                    </div>
                    {website && (
                      <div>
                        <p className="text-muted-foreground">{t("leads.convert.website")}</p>
                        <p className="font-medium text-foreground">{website}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3 rounded-md border border-border p-4">
                  <h4 className="text-sm font-semibold text-foreground">
                    {t("leads.convert.adminSection")}
                  </h4>
                  <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                    <div>
                      <p className="text-muted-foreground">{t("leads.form.name")}</p>
                      <p className="font-medium text-foreground">
                        {adminFirstName} {adminLastName}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t("leads.table.email")}</p>
                      <p className="font-medium text-foreground">{lead.email}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t("leads.convert.department")}</p>
                      <p className="font-medium text-foreground">{department}</p>
                    </div>
                    {jobTitle && (
                      <div>
                        <p className="text-muted-foreground">{t("leads.convert.jobTitle")}</p>
                        <p className="font-medium text-foreground">{jobTitle}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <LeadStatusBadge status={lead.status} />
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <LeadStatusBadge status="WON" />
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={step > 1 ? () => setStep((s) => (s - 1) as 1 | 2 | 3) : handleClose}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              {step > 1 ? t("common.back") : t("common.cancel")}
            </button>

            {step < 3 ? (
              <button
                type="button"
                onClick={() => setStep((s) => (s + 1) as 1 | 2 | 3)}
                disabled={step === 1 ? !canProceedStep1 : !canProceedStep2}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {t("common.next")}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {loading ? t("common.loading") : t("leads.convert.confirm")}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
